import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import KivoBall from '../components/KivoBall';
import KvContextMeter from '../components/KvContextMeter';
import ToolPickerSheet, { type ToolPick } from '../components/ToolPickerSheet';
import { useDictation } from '../hooks/useDictation';
import { STATIC_SYSTEM_PROMPT, STATIC_PROMPT_VERSION } from '../aidata/staticSystemPrompt';
import { buildDynamicContext } from '../aidata/buildDynamicContext';
import { dispatchTool, encodeToolResult, type DispatchResult } from '../aidata/toolDispatcher';
import { truncateHistory } from '../aidata/historyTruncate';
import { useUser } from '../contexts/UserContext';
import { useJobs } from '../contexts/JobsContext';
import { useModalHost } from '../contexts/ModalHostContext';
import { navigateTo } from '../utils/navigation';
import { modelMirrorBaseUrl } from '../api/runtime-config';
import { useModelLoadWatchdog } from '../hooks/useModelLoadWatchdog';
import {
  notifyModelFailed,
  notifyModelReady,
  notifyModelStalled,
} from '../utils/modelLoadAlerts';

// ─── Types ────────────────────────────────────────────────────────────────────
interface ChatMsg { role: 'system' | 'user' | 'assistant'; content: string; }
interface ProductItem { name: string; brand: string; price: string; why: string; }
interface RoutineItem { step: string; note: string; }
interface DisplayMsg {
  role: 'user' | 'assistant';
  content: string;
  isStreaming?: boolean;
  isToolCallStreaming?: boolean;
  products?: ProductItem[];
  routine?: RoutineItem[];
  actions?: string[];
  citations?: string[];
  upsell?: { reason: string; upgradeUrl: string };
}
type BallState = 'idle' | 'thinking' | 'tool_calling' | 'streaming' | 'done';
type InitPhase = 'splash' | 'loading' | 'done';
declare global { interface Window { kivoBall?: { set(s: BallState, el: HTMLElement | null): void; idle(): void; }; } }

const SYSTEM_PROMPT = STATIC_SYSTEM_PROMPT;

// ─── Loading steps ────────────────────────────────────────────────────────────
const CONTEXT_WINDOW = 8192;

const LOAD_STEPS = [
  { label: 'Establishing secure context', sub: 'WebGPU · on-device inference' },
  { label: 'Loading tokenizer',           sub: 'Bonsai 1.7B ONNX · vocabulary' },
  { label: 'Downloading model weights',   sub: '~470 MB · ternary quantization' },
  { label: 'Compiling WebGPU shaders',    sub: '1.58-bit · q2f16' },
  { label: 'Warming up inference engine', sub: 'pre-seeding context window' },
  { label: 'All systems ready',           sub: 'kivo.skin · on-device' },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function cleanModelOutput(raw: string): string {
  return raw
    .replace(/^\s*<context>[\s\S]*?<\/context>\s*/, '')
    .replace(/^\s*<\/context>\s*/, '')
    .replace(/^\s*<tool_result>[\s\S]*?<\/tool_result>\s*/, '')
    .replace(/<\|im_(start|end)\|>[^\n]*\n?/g, '')
    .replace(/<\|(?:assistant|system|user)\|>\s*/g, '')
    .replace(/^(?:assistant|system|user)\s*[:\n]+/i, '')
    .trimStart();
}

function parseToolCall(text: string): { name: string; args: Record<string, string> } | null {
  const match = /<tool_call>([\s\S]*?)<\/tool_call>/.exec(text);
  if (!match) return null;
  try {
    const p = JSON.parse(match[1].trim()) as { name: string; arguments?: Record<string, string> };
    return { name: p.name, args: p.arguments ?? {} };
  } catch { return null; }
}

function stabilizeStreamingMd(s: string): string {
  let out = s;
  if (((out.match(/```/g) ?? []).length) % 2 === 1) out += '\n```';
  const noFences = out.replace(/```/g, '');
  if (((noFences.match(/`/g) ?? []).length) % 2 === 1) out += '`';
  if (((out.match(/\*\*/g) ?? []).length) % 2 === 1) out += '**';
  if (((out.match(/__/g) ?? []).length) % 2 === 1) out += '__';
  return out;
}

function mapToolResultToUI(toolName: string, result: unknown): { products?: ProductItem[]; routine?: RoutineItem[] } {
  if (toolName === 'get_products') {
    const r = result as { recommendations?: { name: string; brand: string; price: string; kivo_note?: string }[] };
    const recommendations = r.recommendations ?? [];
    return { products: recommendations.map(x => ({ name: x.name, brand: x.brand, price: x.price, why: x.kivo_note ?? '' })) };
  }
  if (toolName === 'get_routine') {
    const r = result as { steps?: { step: string; product_type?: string }[] };
    const steps = r.steps ?? [];
    return { routine: steps.map(s => ({ step: s.step, note: s.product_type ?? '' })) };
  }
  return {};
}

function lastDisplayMsg(messages: DisplayMsg[]): DisplayMsg | undefined {
  const lastIndex = messages.length - 1;
  return lastIndex >= 0 ? messages[lastIndex] : undefined;
}

function workerStatusText(data: unknown): string {
  return typeof data === 'string' ? data : '';
}

function toolArgString(value: unknown): string {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return JSON.stringify(value);
}

function fmtBytes(b: number): string {
  if (b >= 1e9) return `${(b / 1e9).toFixed(2)} GB`;
  if (b >= 1e6) return `${(b / 1e6).toFixed(1)} MB`;
  if (b >= 1e3) return `${(b / 1e3).toFixed(0)} KB`;
  return `${b} B`;
}

function shortName(path: string): string {
  const n = path.split('/').pop() ?? path;
  return n.length > 42 ? `…${n.slice(-40)}` : n;
}

function getDefaultActions(toolName?: string): string[] {
  switch (toolName) {
    case 'get_products': return ['Compare these options', 'AM version too', 'Add to routine'];
    case 'get_routine': return ['Save to my routine', 'Swap a product', 'Show me products'];
    case 'analyze_skin': return ['What caused this?', 'Recommended products', 'Calming routine'];
    case 'check_ingredient': return ['Show products with this', 'Can I layer with retinol?', 'Safe for sensitive skin?'];
    case 'ai_face_scan':
    case 'ai_premium_face_scan': return ['Check status', 'See history', 'What will the result show?'];
    case 'product_solo_analysis': return ['Show ingredients', 'Find a similar product', 'Add to favourites'];
    case 'product_compare': return ['Which suits me better?', 'Pair recommendations', 'Save the winner'];
    case 'product_compatibility': return ['Why these two?', 'Suggest alternatives', 'AM/PM split'];
    case 'weather_rating': return ['What SPF do I need?', 'Routine for today', 'AQI tips'];
    case 'get_user_history': return ['Open the latest', 'Compare two scans', 'Trends over time'];
    case 'get_user_favorites': return ['Build a routine from these', 'Show ingredient overlaps', 'Compare two'];
    case 'get_product_info': return ['Compare with another', 'Show ingredient list', 'Is this for my skin?'];
    case 'get_ingredient_info': return ['Products with this', 'What pairs with it?', 'What does it do?'];
    case 'get_products_by_keyword': return ['Pick one', 'Narrow by skin type', 'Compare two'];
    case 'get_job_status': return ['Open the result', 'Cancel the job', 'Estimate'];
    case 'remove_job_from_memory': return ['Show history', 'Recommend next steps'];
    case 'update_user_preferences': return ['Re-run analysis', 'See what changed', 'Build a fresh routine'];
    default: return ['Recommend products', 'Build a routine', 'Explain my results'];
  }
}

function getThinkingText(toolName: string): string {
  const map: Record<string, string> = {
    analyze_skin: 'Analysing your skin profile…',
    get_products: 'Searching product database…',
    get_routine: 'Building your routine…',
    check_ingredient: 'Looking up ingredient data…',
    ai_face_scan: 'Opening face scan…',
    ai_premium_face_scan: 'Opening premium face scan…',
    product_solo_analysis: 'Opening product scanner…',
    product_compare: 'Opening product compare…',
    product_compatibility: 'Opening compatibility checker…',
    weather_rating: 'Opening weather analyzer…',
    update_user_preferences: 'Opening preferences…',
    get_user_history: 'Loading your history…',
    get_user_favorites: 'Loading your favourites…',
    get_product_info: 'Looking up product…',
    get_ingredient_info: 'Looking up ingredient…',
    get_products_by_keyword: 'Searching products…',
    get_job_status: 'Checking job status…',
    remove_job_from_memory: 'Updating my memory…',
  };
  return map[toolName] ?? 'Thinking…';
}

function getToolCitations(toolName: string, args: Record<string, string>): string[] {
  switch (toolName) {
    case 'get_products': return ['product database', [args.category, args.skin_type].filter(Boolean).join(' · ')].filter(Boolean);
    case 'get_routine': return ['routine library', [args.skin_type, args.time_of_day === 'morning' ? 'AM' : 'PM'].filter(Boolean).join(' · ')].filter(Boolean);
    case 'analyze_skin': return ['skin analysis', args.job_id ? `job ${args.job_id.slice(0, 8)}` : ''].filter(Boolean);
    case 'check_ingredient': return ['ingredient database', args.ingredient].filter(Boolean);
    default: return [];
  }
}

/** EU AI Act Art. 50(1): tell the user, at first interaction and at the start of
 *  every new chat, that they are talking to an AI. Shared copy with mobile. */
export const AI_CHAT_NOTICE =
  "You're chatting with an AI assistant. It can make mistakes and does not give medical advice.";

// ─── Init screens ─────────────────────────────────────────────────────────────

function SplashScreen({ onAccept }: { onAccept: () => void }) {
  return (
    <div className="kv-splash">
      <div className="kv-splash__inner">
        <KivoBall state="idle" size={180} />
        <h1 className="kv-splash__brand">
          <span className="t-teal">kivo</span><span style={{ color: 'var(--c-charcoal)' }}>.skin</span>
        </h1>
        <p className="kv-splash__tagline">Your skin, <em>understood.</em></p>
        <p className="kv-splash__body">
          Kivo runs entirely on your device. Photos, prompts and analyses stay local unless you explicitly share them. We never train on your data.
        </p>
        <p className="kv-splash__body kv-ai-notice" role="note">{AI_CHAT_NOTICE}</p>
        <button onClick={onAccept} className="kv-splash__cta">
          I understand, continue
        </button>
      </div>
    </div>
  );
}

/** sessionStorage flag telling the next page load to start at the mirror. */
export const PREFER_MIRROR_KEY = 'kivo_model_prefer_mirror';

function LoadingScreen({ step, fileDownloads, downloadSpeed, stalled, usingMirror }: {
  step: number;
  fileDownloads: Record<string, { progress: number; loaded: number; total: number }>;
  downloadSpeed: number;
  stalled: boolean;
  usingMirror: boolean;
}) {
  const hasMirror = modelMirrorBaseUrl() !== undefined;

  const retryElsewhere = () => {
    // Reloading genuinely resumes: every file that finished is in the browser's
    // cache, so a fresh worker only re-fetches what is missing. The flag is
    // what makes it re-fetch from somewhere new.
    try {
      if (hasMirror && !usingMirror) {
        window.sessionStorage.setItem(PREFER_MIRROR_KEY, '1');
      }
    } catch {
      /* private mode: a plain reload is still better than nothing */
    }
    window.location.reload();
  };
  const fileEntries = Object.entries(fileDownloads).filter(([, f]) => f.total > 0);
  const totalLoaded = fileEntries.reduce((s, [, f]) => s + f.loaded, 0);
  const totalBytes = fileEntries.reduce((s, [, f]) => s + f.total, 0);
  const dlFraction = totalBytes > 0 ? totalLoaded / totalBytes : 0;

  const progress = step < 2
    ? Math.min(1, (step + 0.5) / LOAD_STEPS.length)
    : step === 2
      ? Math.min(1, (2 + dlFraction) / LOAD_STEPS.length)
      : Math.min(1, (step + 0.5) / LOAD_STEPS.length);

  const sortedFiles = fileEntries.sort(([, a], [, b]) => {
    if (a.progress < 100 && b.progress === 100) return -1;
    if (a.progress === 100 && b.progress < 100) return 1;
    return b.total - a.total;
  }).slice(0, 8);

  return (
    <div className="kv-loading">
      <div className="kv-loading__inner">
        <KivoBall state="idle" size={128} />
        <p className="kv-load-label">Initializing</p>
        <h2 className="kv-load-title">Preparing your assistant</h2>

        <div className="kv-steps">
          {LOAD_STEPS.map((s, i) => {
            const state = i < step ? 'done' : i === step ? 'active' : 'pending';
            return (
              <div key={i} className={`kv-step${state === 'pending' ? ' kv-step--pending' : ''}`}>
                <span className={`kv-step__dot kv-step__dot--${state}`}>
                  {state === 'done' && (
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="var(--c-cream)" strokeWidth="3">
                      <path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                  {state === 'active' && <span className="kv-step__pulse" />}
                </span>
                <div className="kv-step__text">
                  <div className={`kv-step__label${state === 'active' ? ' kv-step__label--active' : ''}`}>{s.label}</div>
                  <div className="kv-step__sub">{s.sub}</div>
                </div>
                {state === 'active' && i !== 2 && (
                  <div className="kv-step__shimmer">
                    <div className="kv-shimmer" style={{ height: '100%', width: '100%' }} />
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {usingMirror && !stalled && (
          <p className="kv-load-source">Downloading from our backup server</p>
        )}

        {stalled && (
          <div className="kv-load-stalled" role="status">
            <span>
              {hasMirror && !usingMirror
                ? 'This is taking longer than it should — you can try another source.'
                : 'Nothing has arrived for a while — check your connection.'}
            </span>
            <button
              type="button"
              className="kv-load-stalled__retry"
              onClick={retryElsewhere}
            >
              {hasMirror && !usingMirror ? 'Try another source' : 'Retry'}
            </button>
          </div>
        )}

        <div className="kv-progress">
          <div className="kv-progress__bar">
            <div className="kv-progress__fill" style={{ width: `${progress * 100}%` }} />
          </div>
          <div className="kv-progress__meta">
            <span>{Math.round(progress * 100)}%</span>
            <span>
              {step === 2 && totalBytes > 0
                ? `${fmtBytes(totalLoaded)} / ${fmtBytes(totalBytes)}${downloadSpeed > 0 ? ` · ${fmtBytes(downloadSpeed)}/s` : ''}`
                : '~1.1 GB · WebGPU · on-device'}
            </span>
          </div>
        </div>

        {step === 2 && sortedFiles.length > 0 && (
          <div className="kv-files">
            {sortedFiles.map(([key, f]) => (
              <div key={key}>
                <div className="kv-file__row">
                  <span className={`kv-file__name${f.progress === 100 ? ' kv-file__name--done' : ''}`}>{shortName(key)}</span>
                  <span className="kv-file__size">
                    {f.total > 0 ? `${fmtBytes(f.loaded)} / ${fmtBytes(f.total)}` : `${Math.round(f.progress)}%`}
                    {f.progress === 100 && <span className="kv-file__check">✓</span>}
                  </span>
                </div>
                <div className="kv-file__bar">
                  <div
                    className={`kv-file__fill${f.progress === 100 ? ' kv-file__fill--done' : ' kv-file__fill--partial'}`}
                    style={{ width: `${f.progress}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Chat sub-components ──────────────────────────────────────────────────────

const ThinkingBubble = React.memo(function ThinkingBubble({ status, inputTokens }: { status: string; inputTokens?: number }) {
  // Live elapsed counter so the user can see something is happening during
  // the silent prefill window (Bonsai 1.7B prefill on WebGPU can take 3-15s
  // for a ~1k token prompt depending on hardware).
  const [elapsed, setElapsed] = React.useState(0);
  React.useEffect(() => {
    const start = performance.now();
    const id = window.setInterval(() => {
      setElapsed(Math.round((performance.now() - start) / 100) / 10);
    }, 150);
    return () => { window.clearInterval(id); };
  }, []);

  // Progressive copy so a long prefill doesn't feel frozen.
  let phase = status;
  if (status === 'Thinking…') {
    if (elapsed < 2) phase = 'Reading your message…';
    else if (elapsed < 8) phase = 'Preparing response…';
    else phase = 'Still working — long prompt prefill…';
  }

  return (
    <div className="kv-thinking">
      <div className="kv-thinking__bubble">
        <div className="kv-thinking__dots">
          {[0, 1, 2].map(i => (
            <span key={i} className="kv-thinking__dot" style={{ animationDelay: `${i * 0.15}s` }} />
          ))}
        </div>
        <span className="kv-thinking__status">{phase}</span>
        <span className="kv-thinking__meta">
          {inputTokens ? `${inputTokens.toLocaleString()} tok` : ''}
          {inputTokens ? ' · ' : ''}
          {elapsed.toFixed(1)}s
        </span>
        <div className="kv-thinking__shimmer" />
      </div>
    </div>
  );
});

const UserMessage = React.memo(function UserMessage({ children }: { children: React.ReactNode }) {
  return (
    <div className="kv-msg-user">
      <div className="kv-msg-user__bubble">{children}</div>
    </div>
  );
});

const AssistantMessage = React.memo(function AssistantMessage({ children, citations }: { children: React.ReactNode; citations?: string[]; streaming?: boolean }) {
  return (
    <div className="kv-msg-assistant">
      <div className="kv-msg-assistant__inner">
        <div className="kv-msg-assistant__bubble">{children}</div>
        {citations && citations.length > 0 && (
          <div className="kv-citations">
            {citations.map((c, i) => (
              <span key={i} className="kv-citation">
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 2 4 6v6c0 5 3.5 9 8 10 4.5-1 8-5 8-10V6l-8-4z" strokeLinejoin="round"/>
                </svg>
                {c}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
});

const ProductCard = React.memo(function ProductCard({ name, brand, price, why }: ProductItem) {
  return (
    <div className="kv-product">
      <div className="kv-product__thumb">
        <div className="kv-product__thumb-fill" />
        <div className="kv-product__thumb-cap" />
      </div>
      <div className="kv-product__body">
        <div className="kv-product__brand">{brand}</div>
        <div className="kv-product__name">{name}</div>
        <div className="kv-product__why">{why}</div>
        <div className="kv-product__price">{price}</div>
      </div>
    </div>
  );
});

const RoutineCard = React.memo(function RoutineCard({ items }: { items: RoutineItem[] }) {
  return (
    <div className="kv-routine">
      {items.map((it, i) => (
        <div key={i} className="kv-routine__step">
          <span className={`kv-routine__num${i === 0 ? ' kv-routine__num--first' : ' kv-routine__num--rest'}`}>{i + 1}</span>
          <div>
            <div className="kv-routine__label">{it.step}</div>
            <div className="kv-routine__note">{it.note}</div>
          </div>
        </div>
      ))}
    </div>
  );
});

const PremiumUpsellCard = React.memo(function PremiumUpsellCard({ reason, upgradeUrl }: { reason: string; upgradeUrl: string }) {
  return (
    <div className="kv-upsell">
      <div className="kv-upsell__row">
        <div className="kv-upsell__icon">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
          </svg>
        </div>
        <div className="kv-upsell__body">
          <div className="kv-upsell__title">Premium feature</div>
          <div className="kv-upsell__reason">{reason}</div>
        </div>
        <a href={upgradeUrl} className="kv-upsell__btn">Upgrade</a>
      </div>
    </div>
  );
});

const QuickActions = React.memo(function QuickActions({ items, onPick }: { items: string[]; onPick?: (t: string) => void }) {
  return (
    <div className="kv-actions">
      {items.map((it, i) => (
        <button key={i} onClick={() => onPick?.(it)} className="kv-action-btn">{it}</button>
      ))}
    </div>
  );
});

function SuggestedPrompt({ icon, label, sub, onClick, delay = 0 }: { icon: React.ReactNode; label: string; sub: string; onClick: () => void; delay?: number }) {
  return (
    <button onClick={onClick} className="kv-suggested" style={{ animationDelay: `${delay}ms` }}>
      <span className="kv-suggested__icon">{icon}</span>
      <span className="kv-suggested__label">{label}</span>
      <span className="kv-suggested__sub">{sub}</span>
    </button>
  );
}

function EmptyState({ onPick, onOpenTools }: { onPick: (t: string) => void; onOpenTools: () => void }) {
  const hour = new Date().getHours();
  const greeting = hour < 5 ? 'tonight' : hour < 12 ? 'this morning' : hour < 18 ? 'today' : 'tonight';
  return (
    <div className="kv-empty">
      <div className="kv-empty__inner">
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 7,
            padding: '6px 13px',
            borderRadius: 999,
            background: 'var(--c-teal-weak)',
            border: '1px solid rgba(42,164,166,0.28)',
            font: '500 11px/1 var(--f-mono, monospace)',
            letterSpacing: '.14em',
            textTransform: 'uppercase',
            color: 'var(--c-deep-teal)',
            marginBottom: 'var(--s-4)',
          }}
        >
          <span
            className="kv-pulse-dot"
            style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--c-kivo-teal)' }}
          />
          Kivo AI
        </span>
        <h1 className="kv-empty__title">
          How is your skin <span className="t-teal">{greeting}</span>?
        </h1>
        <p className="kv-empty__sub">
          Your skin’s private companion. It knows your scans and answers in plain
          language — ask about products, ingredients or routines.
        </p>
        <p className="kv-empty__sub kv-ai-notice" role="note">{AI_CHAT_NOTICE}</p>
        <div className="kv-empty__grid">
          <SuggestedPrompt
            delay={200}
            onClick={() => { onPick('Build me a simple AM routine for my skin.'); }}
            icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" strokeLinecap="round"/></svg>}
            label="Build me an AM routine"
            sub="Based on your skin type"
          />
          <SuggestedPrompt
            delay={300}
            onClick={() => { onPick('Recommend a vitamin C serum for my skin, under €30, fragrance-free.'); }}
            icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2" strokeLinecap="round"/></svg>}
            label="Vitamin C serum pick"
            sub="Under €30, fragrance-free"
          />
          <SuggestedPrompt
            delay={400}
            onClick={() => { onPick('What does niacinamide actually do for skin?'); }}
            icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M20 7L9 18l-5-5" strokeLinecap="round" strokeLinejoin="round"/></svg>}
            label="What does niacinamide do?"
            sub="Explain the science"
          />
          <SuggestedPrompt
            delay={500}
            onClick={() => { onPick('I have oily skin and keep breaking out — what should my routine look like?'); }}
            icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="12" cy="12" r="9"/><path d="M12 8v4M12 16h.01" strokeLinecap="round"/></svg>}
            label="Oily + acne-prone help"
            sub="Routine recommendation"
          />
        </div>
        <button onClick={onOpenTools} className="kv-empty__tools">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 5v14M5 12h14" strokeLinecap="round"/>
          </svg>
          Or pick a quick action
        </button>
        <div
          className="t-mute"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 6,
            marginTop: 'var(--s-4)',
            font: '400 11px/1 var(--f-text)',
          }}
        >
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--c-kivo-teal)' }} />
          Runs privately on your device · free to chat
        </div>
      </div>
    </div>
  );
}

interface ComposerProps {
  value: string;
  onChange: (v: string) => void;
  onSubmit: () => void;
  onOpenTools: () => void;
  disabled?: boolean;
}

function Composer({ value, onChange, onSubmit, onOpenTools, disabled }: ComposerProps) {
  const ref = useRef<HTMLTextAreaElement>(null);

  const handleTranscript = useCallback((t: string) => { onChange(t); }, [onChange]);
  const handleError = useCallback((err: string) => {
    if (err === 'not-allowed' || err === 'service-not-allowed') {
      console.warn('[dictation] permission denied');
    }
  }, []);
  const dictation = useDictation({ onTranscript: handleTranscript, onError: handleError });

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 240)}px`;
  }, [value]);

  const active = !disabled && value.trim();
  const micTitle = !dictation.supported
    ? 'Voice input not supported in this browser'
    : dictation.recording ? 'Stop dictation' : 'Start dictation';

  return (
    <div className="kv-composer">
      <button
        type="button"
        title="Quick actions"
        aria-label="Open quick actions"
        onClick={onOpenTools}
        className="kv-composer__btn kv-composer__btn--plus"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M12 5v14M5 12h14" strokeLinecap="round"/>
        </svg>
      </button>
      <textarea
        ref={ref}
        rows={1}
        value={value}
        disabled={disabled}
        onChange={e => { onChange(e.target.value); }}
        onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); onSubmit(); } }}
        placeholder={disabled ? 'Generating…' : (dictation.recording ? 'Listening…' : 'Ask Kivo about your skin…')}
        className="kv-composer__textarea"
      />
      <button
        type="button"
        title={micTitle}
        aria-pressed={dictation.recording}
        aria-disabled={!dictation.supported}
        onClick={() => {
          if (!dictation.supported) return;
          dictation.toggle(value);
        }}
        className={`kv-composer__btn${dictation.recording ? ' kv-composer__btn--rec' : ''}${!dictation.supported ? ' is-disabled' : ''}`}
      >
        {dictation.recording ? (
          <span className="kv-voice-bars">
            {[0, 1, 2, 3].map(i => (
              <span key={i} className="kv-voice-bar" style={{ animationDelay: `${i * 0.12}s` }} />
            ))}
          </span>
        ) : (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7">
            <rect x="9" y="3" width="6" height="12" rx="3"/>
            <path d="M5 11a7 7 0 0 0 14 0M12 18v3" strokeLinecap="round"/>
          </svg>
        )}
      </button>
      <button
        type="button"
        onClick={onSubmit}
        disabled={!active}
        className={`kv-composer__send${active ? ' kv-composer__send--on' : ' kv-composer__send--off'}`}
      >
        {disabled && value.trim()
          ? <span className="kv-composer__spin" />
          : <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M5 12h14M13 6l6 6-6 6" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
        }
      </button>
    </div>
  );
}

// ─── Header ──────────────────────────────────────────────────────────────────

function ChatHeader({
  ballState, ctxUsed, isGenerating, onStop, onClear, canClear, onExit,
}: {
  ballState: BallState;
  ctxUsed: number;
  isGenerating: boolean;
  onStop: () => void;
  onClear: () => void;
  canClear: boolean;
  onExit: () => void;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  useEffect(() => {
    if (!menuOpen) return;
    const onDoc = () => { setMenuOpen(false); };
    window.addEventListener('click', onDoc);
    return () => { window.removeEventListener('click', onDoc); };
  }, [menuOpen]);

  return (
    <div className="kv-header">
      <div className="kv-header__inner">
        <button
          type="button"
          className="kv-header__left kv-header__exit"
          onClick={onExit}
          aria-label="Exit AI chat"
        >
          <KivoBall state={ballState} size={32} />
          <div className="kv-header__label">
            <div className="kv-header__brand">
              <span className="t-teal">kivo</span><span style={{ color: 'var(--c-charcoal)' }}>.skin</span>
            </div>
            {/* Build stamp so you can verify the page is running the latest worker bundle. */}
            <div className="kv-header__sub">on-device · {STATIC_PROMPT_VERSION}</div>
          </div>
        </button>
        <div className="kv-header__right">
          <KvContextMeter used={ctxUsed} total={CONTEXT_WINDOW} />
          {isGenerating && (
            <button type="button" onClick={onStop} className="kv-ctrl-stop">
              Stop
            </button>
          )}
          <div className="kv-header__kebab-wrap">
            <button
              type="button"
              className="kv-ctrl-kebab"
              aria-label="More options"
              onClick={(e) => { e.stopPropagation(); setMenuOpen((v) => !v); }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                <circle cx="5" cy="12" r="1.6"/><circle cx="12" cy="12" r="1.6"/><circle cx="19" cy="12" r="1.6"/>
              </svg>
            </button>
            {menuOpen && (
              <div
                className="kv-kebab-menu"
                role="menu"
                tabIndex={-1}
                onClick={(e) => { e.stopPropagation(); }}
                onKeyDown={(e) => { e.stopPropagation(); }}
              >
                <button type="button" disabled={!canClear} onClick={() => { setMenuOpen(false); onClear(); }}>
                  Clear conversation
                </button>
                <button type="button" onClick={() => { setMenuOpen(false); onExit(); }}>
                  Exit chat
                </button>
                <a href="/ai-disclaimer" onClick={() => { setMenuOpen(false); }}>About on-device</a>
                <a href="/support" onClick={() => { setMenuOpen(false); }}>Help & feedback</a>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function AIChatPage(): React.JSX.Element {
  const [initPhase, setInitPhase] = useState<InitPhase>('splash');
  const [loadingStep, setLoadingStep] = useState(0);
  const [fileDownloads, setFileDownloads] = useState<Record<string, { progress: number; loaded: number; total: number }>>({});
  const [downloadSpeed, setDownloadSpeed] = useState(0);
  const [usingMirror, setUsingMirror] = useState(false);

  const { user, plan, scannerPrefs, compatPrefs, refreshPrefs } = useUser();
  const jobsCtx = useJobs();
  const { jobs, refreshStatuses } = jobsCtx;
  const modalCtx = useModalHost();

  const [displayMessages, setDisplayMessages] = useState<DisplayMsg[]>([]);
  const [input, setInput] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [thinking, setThinking] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [ballState, setBallState] = useState<BallState>('idle');
  const [ctxUsed, setCtxUsed] = useState(0);
  const [toolPickerOpen, setToolPickerOpen] = useState(false);

  const workerRef = useRef<Worker | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const historyRef = useRef<ChatMsg[]>([]);
  const truncatedRef = useRef(false);
  const accRef = useRef('');
  const lastToolNameRef = useRef<string | undefined>(undefined);
  const lastToolResultRef = useRef<unknown>(undefined);
  const lastToolDispatchRef = useRef<DispatchResult | undefined>(undefined);
  const initPhaseRef = useRef<InitPhase>('splash');
  const workerWarmedRef = useRef(false);
  const toolCallDetectedRef = useRef(false);
  const lastToolArgsRef = useRef<Record<string, string>>({});
  const batchTimeoutRef = useRef<number | null>(null);
  const fileBytesRef = useRef<Record<string, number>>({});
  const speedRef = useRef<{ time: number; bytes: number }>({ time: 0, bytes: 0 });
  const dispatchAbortRef = useRef<AbortController | null>(null);

  useEffect(() => { initPhaseRef.current = initPhase; }, [initPhase]);

  // A stalled download and a slow one look identical on a bar that has stopped
  // moving, so the wait was previously indistinguishable from a hang. The
  // watchdog only runs while the model is actually coming down.
  const { stalled, markProgress } = useModelLoadWatchdog(
    initPhase === 'loading' && loadingStep < 4,
    notifyModelStalled,
  );

  // Refs so the one-shot worker effect below (empty deps, deliberately — see
  // its comment) can reach the latest values without being torn down.
  const markProgressRef = useRef(markProgress);
  useEffect(() => { markProgressRef.current = markProgress; }, [markProgress]);

  const ctxSnapshotRef = useRef({ user, plan, scannerPrefs, compatPrefs, jobs });
  useEffect(() => {
    ctxSnapshotRef.current = { user, plan, scannerPrefs, compatPrefs, jobs };
  }, [user, plan, scannerPrefs, compatPrefs, jobs]);

  const composeDynamic = useCallback(() => {
    const s = ctxSnapshotRef.current;
    return buildDynamicContext({
      user: s.user,
      plan: s.plan,
      scannerPrefs: s.scannerPrefs,
      compatPrefs: s.compatPrefs,
      jobs: s.jobs,
    });
  }, []);

  const buildDispatchCtx = useCallback(
    () => ({ plan, jobs: jobsCtx, refreshPrefs, modal: modalCtx }),
    [plan, jobsCtx, refreshPrefs, modalCtx],
  );

  useEffect(() => {
    document.body.classList.add('kv-assistant');
    return () => { document.body.classList.remove('kv-assistant'); };
  }, []);

  const stuckToBottomRef = useRef(true);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => {
      const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
      stuckToBottomRef.current = distance < 80;
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => { el.removeEventListener('scroll', onScroll); };
  }, []);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || !stuckToBottomRef.current) return;
    const anyStreaming = displayMessages.some(m => Boolean(m.isStreaming) || Boolean(m.isToolCallStreaming));
    el.scrollTo({ top: el.scrollHeight, behavior: anyStreaming ? 'auto' : 'smooth' });
  }, [displayMessages, thinking]);

  const handleToolCall = useCallback(async (toolCall: { name: string; args: Record<string, string> }, rawOutput: string) => {
    lastToolNameRef.current = toolCall.name;
    lastToolArgsRef.current = toolCall.args;
    toolCallDetectedRef.current = false;

    setThinking(getThinkingText(toolCall.name));
    setDisplayMessages(prev => {
      const up = [...prev];
      const last = lastDisplayMsg(up);
      if (last?.role === 'assistant' && last.isToolCallStreaming) up.splice(up.length - 1, 1);
      return up;
    });

    const controller = new AbortController();
    dispatchAbortRef.current = controller;
    const dispatch: DispatchResult = await dispatchTool(
      toolCall.name,
      toolCall.args,
      buildDispatchCtx(),
      controller.signal,
    );
    dispatchAbortRef.current = null;

    lastToolResultRef.current = dispatch.ok ? dispatch.result : { error: dispatch.error, code: dispatch.code };
    lastToolDispatchRef.current = dispatch;
    const toolResultContent = encodeToolResult(dispatch);

    // Bake the dynamic <context> block into the user message AT SEND TIME so
    // history bytes stay byte-stable across turns — that's what lets the
    // worker's persistent KV cache survive multi-turn conversations.
    const ctxForToolResult = composeDynamic();
    const userTurnContent = ctxForToolResult + toolResultContent;

    historyRef.current = [
      ...historyRef.current,
      { role: 'assistant', content: rawOutput },
      { role: 'user', content: userTurnContent },
    ];

    setThinking(null);
    if (batchTimeoutRef.current !== null) { cancelAnimationFrame(batchTimeoutRef.current); batchTimeoutRef.current = null; }
    accRef.current = '';
    setDisplayMessages(prev => [...prev, { role: 'assistant', content: '', isStreaming: true }]);

    // The next worker turn: the new "userMessage" is the tool_result (with
    // context already baked in). We've already pushed it onto historyRef
    // above, so trim the last turn off the history sent to the worker — it's
    // the same content as userMessage.
    const tail = historyRef.current.slice(0, -1) as { role: 'user' | 'assistant'; content: string }[];
    const { history: sendHistory, didTruncate } = truncateHistory(tail);
    if (didTruncate && !truncatedRef.current) {
      truncatedRef.current = true;
      workerRef.current?.postMessage({ type: 'reset' });
    }
    // Tool-call follow-ups always invalidate the persistent KV cache because
    // the cache extended into the model's <tool_call> response while history
    // now also has the synthetic <tool_result> user turn injected — bytes
    // diverge. Reset so the next prefill rebuilds from scratch.
    workerRef.current?.postMessage({ type: 'reset' });
    requestAnimationFrame(() => {
      workerRef.current?.postMessage({
        type: 'generate',
        wireFormat: 'v2',
        data: {
          system: SYSTEM_PROMPT,
          history: sendHistory,
          userMessage: userTurnContent,
        },
      });
    });
  }, [composeDynamic, buildDispatchCtx]);

  // Ref pattern: the worker `onmessage` closure needs to call the *latest*
  // handleToolCall, but the worker itself must outlive identity changes of
  // that callback. Previously the effect listed `handleToolCall` in its deps,
  // which made the effect re-run every time `modalCtx` changed (i.e. every
  // time a modal opened or closed) — terminating the loaded worker mid-
  // conversation and emitting "Model not loaded" the next time the user
  // dispatched a tool. Now the effect runs ONCE and reads from the ref.
  const handleToolCallRef = useRef(handleToolCall);
  useEffect(() => { handleToolCallRef.current = handleToolCall; }, [handleToolCall]);

  // Worker init (one-shot — see comment above for why deps must be empty).
  useEffect(() => {
    const worker = new Worker(new URL('../workers/kivoWorker.ts', import.meta.url), { type: 'module' });
    workerRef.current = worker;

    worker.onmessage = async (e: MessageEvent) => {
      const { status, data } = e.data as { status: string; data: unknown };
      switch (status) {
        case 'loading': {
          const msg = workerStatusText(data).toLowerCase();
          if (msg.includes('tokenizer')) setLoadingStep(1);
          else if (msg.includes('model weights')) setLoadingStep(2);
          else if (msg.includes('compiling') || msg.includes('shader')) setLoadingStep(3);
          break;
        }
        case 'progress': {
          const p = data as { file?: string; name?: string; progress?: number; loaded?: number; total?: number; status?: string };
          if (p.progress !== undefined && (p.file || p.name)) {
            const fileKey = p.file ?? p.name ?? '';
            const loaded = p.loaded ?? 0;
            const total = p.total ?? 0;
            setLoadingStep(2);
            markProgressRef.current();
            fileBytesRef.current[fileKey] = loaded;
            const totalLoaded = Object.values(fileBytesRef.current).reduce((s, b) => s + b, 0);
            const now = performance.now();
            const dt = (now - speedRef.current.time) / 1000;
            if (dt >= 0.5) {
              const delta = totalLoaded - speedRef.current.bytes;
              setDownloadSpeed(Math.round(delta / dt));
              speedRef.current = { time: now, bytes: totalLoaded };
            }
            setFileDownloads(prev => ({
              ...prev,
              [fileKey]: { progress: p.progress ?? 0, loaded, total },
            }));
          }
          break;
        }
        case 'source': {
          const src = data as { mirror?: boolean } | undefined;
          setUsingMirror(src?.mirror === true);
          break;
        }
        case 'ready':
          setLoadingStep(4);
          worker.postMessage({
            type: 'warmup',
            data: { systemPrompt: SYSTEM_PROMPT, version: STATIC_PROMPT_VERSION },
          });
          break;
        case 'warmed':
          workerWarmedRef.current = true;
          setLoadingStep(5);
          // Minutes of waiting is long enough that people switch tabs. Silent
          // no-op unless they granted permission *and* the tab is hidden.
          notifyModelReady();
          if (initPhaseRef.current === 'loading') {
            setTimeout(() => { setInitPhase('done'); }, 350);
          }
          break;
        case 'start': {
          accRef.current = '';
          toolCallDetectedRef.current = false;
          setBallState('thinking');
          const startData = data as { inputTokens?: number } | undefined;
          if (typeof startData?.inputTokens === 'number') {
            setCtxUsed(startData.inputTokens);
          }
          break;
        }
        case 'update': {
          const { token, tokenCount, inputTokens } = data as { token: string; tokenCount?: number; inputTokens?: number };
          accRef.current += token;
          if (typeof inputTokens === 'number' && typeof tokenCount === 'number') {
            setCtxUsed(inputTokens + tokenCount);
          }

          if (!toolCallDetectedRef.current && accRef.current.includes('<tool_call')) {
            toolCallDetectedRef.current = true;
            setBallState('tool_calling');
            setThinking('Calling tool…');
            if (batchTimeoutRef.current !== null) {
              cancelAnimationFrame(batchTimeoutRef.current);
              batchTimeoutRef.current = null;
            }
            setDisplayMessages(prev => {
              const up = [...prev];
              const last = lastDisplayMsg(up);
              if (last?.role === 'assistant' && last.isStreaming)
                up[up.length - 1] = { ...last, content: '', isToolCallStreaming: true };
              return up;
            });
            break;
          }
          if (toolCallDetectedRef.current) break;
          setBallState('streaming');

          batchTimeoutRef.current ??= requestAnimationFrame(() => {
            batchTimeoutRef.current = null;
            setDisplayMessages(prev => {
              const up = prev.slice();
              const last = lastDisplayMsg(up);
              if (last?.role === 'assistant' && !last.isToolCallStreaming) {
                up[up.length - 1] = { ...last, content: cleanModelOutput(accRef.current), isStreaming: true };
              }
              return up;
            });
          });
          break;
        }
        case 'complete': {
          const { output, tokenCount, inputTokens } = data as { output: string; tokenCount?: number; inputTokens?: number };
          if (typeof inputTokens === 'number' && typeof tokenCount === 'number') {
            setCtxUsed(inputTokens + tokenCount);
          }
          const cleanOutput = cleanModelOutput(output);
          const toolCall = parseToolCall(cleanOutput);
          if (toolCall) {
            await handleToolCallRef.current(toolCall, cleanOutput);
          } else {
            if (batchTimeoutRef.current !== null) { cancelAnimationFrame(batchTimeoutRef.current); batchTimeoutRef.current = null; }
            setIsGenerating(false);
            setThinking(null);
            const uiExtras = lastToolNameRef.current ? mapToolResultToUI(lastToolNameRef.current, lastToolResultRef.current) : {};
            const actions = getDefaultActions(lastToolNameRef.current);
            const citations = lastToolNameRef.current
              ? getToolCitations(lastToolNameRef.current, lastToolArgsRef.current)
              : undefined;
            const lastDispatch = lastToolDispatchRef.current;
            const upsell = lastDispatch && !lastDispatch.ok && lastDispatch.code === 'requires_premium'
              ? { reason: lastDispatch.error, upgradeUrl: lastDispatch.upgrade_url ?? '/plans' }
              : undefined;
            setDisplayMessages(prev => {
              const up = [...prev];
              const last = lastDisplayMsg(up);
              if (last?.role === 'assistant')
                up[up.length - 1] = {
                  ...last, content: cleanOutput,
                  isStreaming: false,
                  ...uiExtras, actions,
                  ...(citations && citations.length > 0 ? { citations } : {}),
                  ...(upsell ? { upsell } : {}),
                };
              return up;
            });
            // Store the raw output in historyRef. The pipeline already returns
            // a clean `content` string (via `result[0].generated_text.at(-1).content`),
            // so cleanOutput ≈ output for well-behaved turns — but storing the
            // exact bytes the model emitted keeps history aligned with the
            // worker's persistent KV cache, which is what makes turn N+1 fast.
            historyRef.current = [...historyRef.current, { role: 'assistant', content: output }];
            setBallState('done');
            setTimeout(() => { setBallState('idle'); }, 900);
            lastToolNameRef.current = undefined; lastToolResultRef.current = undefined; lastToolArgsRef.current = {};
            lastToolDispatchRef.current = undefined;
          }
          break;
        }
        case 'error': {
          const msg = workerStatusText(data);
          const lower = msg.toLowerCase();
          const fatal = lower.includes('webgpu') || lower.includes('gpu') || lower.includes('not loaded');
          // Always clear in-flight state — leaving thinking=true forever was the
          // "infinite thinking" bug for non-fatal worker errors.
          setIsGenerating(false);
          setThinking(null);
          setBallState('idle');
          if (batchTimeoutRef.current !== null) { cancelAnimationFrame(batchTimeoutRef.current); batchTimeoutRef.current = null; }
          if (fatal) {
            setErrorMsg(msg);
            setLoadingStep(5);
            setInitPhase('loading');
            notifyModelFailed(msg);
          } else {
            setDisplayMessages(prev => {
              const up = [...prev];
              const last = lastDisplayMsg(up);
              if (last?.role === 'assistant' && !last.content) {
                up[up.length - 1] = { ...last, content: `Sorry, I ran into a problem: ${msg}`, isStreaming: false };
              }
              return up;
            });
          }
          break;
        }
      }
    };
    // The worker has no `window`, so the deploy-time mirror URL is read here
    // and handed over. PREFER_MIRROR_KEY is set by the stalled-download retry
    // just before it reloads the page, so the fresh worker starts at the
    // backup instead of the host that had gone quiet.
    let preferMirror = false;
    try {
      preferMirror = window.sessionStorage.getItem(PREFER_MIRROR_KEY) === '1';
      window.sessionStorage.removeItem(PREFER_MIRROR_KEY);
    } catch {
      /* private mode: fall back to normal ordering */
    }
    worker.postMessage({
      type: 'load',
      data: { mirrorBaseUrl: modelMirrorBaseUrl(), preferMirror },
    });
    return () => { worker.terminate(); };
     
  }, []);

  const handleSplashAccept = useCallback(() => {
    setInitPhase('loading');
    if (workerWarmedRef.current) {
      setTimeout(() => { setInitPhase('done'); }, 350);
    }
  }, []);

  const sendMessage = useCallback(async (text: string) => {
    const trimmed = text.trim();
    if (!trimmed || isGenerating || initPhase !== 'done') return;
    setInput(''); setThinking(null);
    lastToolNameRef.current = undefined; lastToolResultRef.current = undefined; lastToolArgsRef.current = {};
    toolCallDetectedRef.current = false;
    setDisplayMessages(prev => [
      ...prev,
      { role: 'user', content: trimmed },
      { role: 'assistant', content: '', isStreaming: true },
    ]);

    try {
      await Promise.race([
        refreshStatuses(),
        new Promise<void>((resolve) => setTimeout(resolve, 500)),
      ]);
    } catch { /* ignore */ }

    // Bake the per-turn dynamic context into the stored user message so the
    // bytes the worker sees on this turn are identical to the bytes it will
    // see when replaying this turn from history on turn N+1. This is the
    // invariant the worker's persistent KV cache relies on.
    const userTurnContent = composeDynamic() + trimmed;
    historyRef.current = [...historyRef.current, { role: 'user', content: userTurnContent }];
    accRef.current = '';
    setIsGenerating(true);

    // Remove the just-pushed turn from the wire history — it'll be sent as
    // userMessage instead.
    const tail = historyRef.current.slice(0, -1) as { role: 'user' | 'assistant'; content: string }[];
    const { history: sendHistory, didTruncate } = truncateHistory(tail);
    if (didTruncate && !truncatedRef.current) {
      truncatedRef.current = true;
      workerRef.current?.postMessage({ type: 'reset' });
    }
    workerRef.current?.postMessage({
      type: 'generate',
      wireFormat: 'v2',
      data: {
        system: SYSTEM_PROMPT,
        history: sendHistory,
        userMessage: userTurnContent,
      },
    });
  }, [isGenerating, initPhase, composeDynamic, refreshStatuses]);

  const handleStop = useCallback(() => {
    // Interrupt the worker AND cancel any in-flight tool dispatch — both can
    // be blocking. Also send `reset` because the partial assistant tokens
    // that ended up in the persistent KV cache no longer match what's stored
    // in historyRef (we don't persist partial responses), so the cache is
    // invalid for the next turn.
    workerRef.current?.postMessage({ type: 'interrupt' });
    workerRef.current?.postMessage({ type: 'reset' });
    dispatchAbortRef.current?.abort();
    setIsGenerating(false);
    setThinking(null);
    setBallState('idle');
    if (batchTimeoutRef.current !== null) { cancelAnimationFrame(batchTimeoutRef.current); batchTimeoutRef.current = null; }
    // Finalise whatever streamed content exists so the user sees a partial reply.
    setDisplayMessages(prev => {
      const up = [...prev];
      const last = lastDisplayMsg(up);
      if (last?.role === 'assistant' && last.isStreaming) {
        up[up.length - 1] = { ...last, isStreaming: false, content: last.content ? last.content : '_(stopped)_' };
      }
      return up;
    });
  }, []);

  const handleExitChat = useCallback(() => {
    const ok = window.confirm('Leaving will end this AI chat session. Do you want to exit?');
    if (!ok) return;

    workerRef.current?.postMessage({ type: 'interrupt' });
    workerRef.current?.postMessage({ type: 'reset' });
    dispatchAbortRef.current?.abort();
    if (batchTimeoutRef.current !== null) {
      cancelAnimationFrame(batchTimeoutRef.current);
      batchTimeoutRef.current = null;
    }
    navigateTo('/');
  }, []);

  const clearConversation = useCallback(() => {
    if (batchTimeoutRef.current !== null) { cancelAnimationFrame(batchTimeoutRef.current); batchTimeoutRef.current = null; }
    dispatchAbortRef.current?.abort();
    accRef.current = '';
    historyRef.current = []; setDisplayMessages([]); setThinking(null); setIsGenerating(false);
    lastToolNameRef.current = undefined; lastToolResultRef.current = undefined;
    truncatedRef.current = false;
    workerRef.current?.postMessage({ type: 'reset' }); setBallState('idle');
    setCtxUsed(0);
  }, []);

  const runToolFromUI = useCallback(async (pick: ToolPick) => {
    if (isGenerating || initPhase !== 'done') return;
    setToolPickerOpen(false);

    const rawOutput = `<tool_call>${JSON.stringify({ name: pick.name, arguments: pick.args })}</tool_call>`;

    const stringArgs: Record<string, string> = {};
    for (const [k, v] of Object.entries(pick.args)) stringArgs[k] = toolArgString(v);

    const argsLabel = Object.keys(stringArgs).length > 0
      ? ` with ${Object.entries(stringArgs).map(([k, v]) => `${k}=${v}`).join(', ')}`
      : '';
    // Bake context into the synthetic user instruction, same as sendMessage,
    // so history bytes stay byte-stable for KV reuse.
    historyRef.current = [
      ...historyRef.current,
      { role: 'user', content: composeDynamic() + `Run the ${pick.name} tool${argsLabel}.` },
    ];

    setIsGenerating(true);
    setBallState('tool_calling');
    setDisplayMessages(prev => [...prev, { role: 'assistant', content: '', isToolCallStreaming: true }]);

    await handleToolCall({ name: pick.name, args: stringArgs }, rawOutput);
  }, [isGenerating, initPhase, handleToolCall, composeDynamic]);

  const chatReady = initPhase === 'done';
  const empty = displayMessages.length === 0 && !thinking;

  return (
    <>
      {!errorMsg && initPhase === 'splash' && <SplashScreen onAccept={handleSplashAccept} />}
      {!errorMsg && initPhase === 'loading' && (
        <LoadingScreen
          step={loadingStep}
          fileDownloads={fileDownloads}
          downloadSpeed={downloadSpeed}
          stalled={stalled}
          usingMirror={usingMirror}
        />
      )}

      {errorMsg && (
        <div className="kv-error">
          <div className="kv-error__icon">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/><path d="M12 8v4M12 16h.01"/>
            </svg>
          </div>
          <div className="kv-error__title">WebGPU not available</div>
          <div className="kv-error__body">{errorMsg}</div>
          <div className="kv-error__hint">Try Chrome 113+ or Edge 113+ on a GPU-capable device.</div>
        </div>
      )}

      {chatReady && !errorMsg && (
        <div className="kv-page">
          <ChatHeader
            ballState={ballState}
            ctxUsed={ctxUsed}
            isGenerating={isGenerating}
            onStop={handleStop}
            onClear={clearConversation}
            canClear={displayMessages.length > 0}
            onExit={handleExitChat}
          />

          <div ref={scrollRef} className="kv-messages">
            <div className="kv-messages__inner">
              {empty ? (
                <EmptyState onPick={sendMessage} onOpenTools={() => { setToolPickerOpen(true); }} />
              ) : (
                <div className="kv-messages__list">
                  {displayMessages.map((m, i) => {
                    if (m.role === 'user') {
                      return <div key={i}><UserMessage>{m.content}</UserMessage></div>;
                    }
                    const showThinking = Boolean(m.isToolCallStreaming) || (Boolean(m.isStreaming) && !m.content);
                    if (showThinking) {
                      return <div key={i}><ThinkingBubble status={thinking ?? 'Thinking…'} inputTokens={ctxUsed} /></div>;
                    }
                    if (!m.content) return null;
                    return (
                      <div key={i}>
                        <AssistantMessage citations={m.citations} streaming={m.isStreaming}>
                          <div className="kv-md">
                            <ReactMarkdown>
                              {m.isStreaming ? stabilizeStreamingMd(m.content) : m.content}
                            </ReactMarkdown>
                          </div>
                          {m.products && m.products.length > 0 && (
                            <div className="kv-products">
                              {m.products.map((p, pi) => <ProductCard key={pi} {...p} />)}
                            </div>
                          )}
                          {m.routine && m.routine.length > 0 && <RoutineCard items={m.routine} />}
                        </AssistantMessage>
                        {m.upsell && !m.isStreaming && <PremiumUpsellCard reason={m.upsell.reason} upgradeUrl={m.upsell.upgradeUrl} />}
                        {m.actions && !m.isStreaming && <QuickActions items={m.actions} onPick={sendMessage} />}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          <div className="kv-composer-wrap">
            <div className="kv-composer-inner">
              <Composer
                value={input}
                onChange={setInput}
                onSubmit={() => sendMessage(input)}
                onOpenTools={() => { setToolPickerOpen(true); }}
                disabled={isGenerating}
              />
              <p className="kv-composer-caption">
                On-device inference · your data never leaves your device
              </p>
            </div>
          </div>
        </div>
      )}

      <ToolPickerSheet
        open={toolPickerOpen}
        onClose={() => { setToolPickerOpen(false); }}
        onPick={runToolFromUI}
        plan={plan}
      />
    </>
  );
}
